import React, { useMemo } from 'react';
import { Button, Alert, Modal } from '@/components/ui';
import { Project } from '../../../types/Project';
import { Task } from '../../../types/Task';

interface CloseProjectModalProps {
  visible: boolean;
  project: Project | null;
  onClose: () => void;
  onConfirm: () => void;
}

const CloseProjectModal: React.FC<CloseProjectModalProps> = ({
  visible,
  project,
  onClose,
  onConfirm
}) => {
  const validationResults = useMemo(() => {
    if (!project) return { canClose: false, messages: [] };

    const messages: string[] = [];

    // Check for missing job number - H200 and CMC/MVI require a job number to be closed
    // Internal, Quantified, and Routine can be closed without a job number
    // Other Revenue also requires a job number
    const requiresJobNumber = project.type === 'H200' || 
                              project.type === 'CMC/MVI' || 
                              project.type === 'Other Revenue';
    
    if (!project.jobNumber && requiresJobNumber) {
      messages.push('Project is missing a job number');
    }

    // Check for active tasks
    const hasActiveTasks = project.tasks?.some(task => 
      task.status === 'ACTIVE' || 
      task.status === 'PLANNED' || 
      task.status === 'ACTIVE_MARKED_AS_COMPLETE'
    );

    if (hasActiveTasks) {
      messages.push('There are active tasks that need to be closed');
    }

    return {
      canClose: messages.length === 0,
      messages
    };
  }, [project]);

  return (
    // A confirm — stays a centered card on phones rather than a full-screen sheet.
    <Modal show={visible} onHide={onClose} mobileVariant="center">
      <Modal.Header closeButton>
        <Modal.Title>Close Project</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {validationResults.messages.length > 0 ? (
          <>
            <Alert variant="warning">
              <Alert.Heading>Cannot close project</Alert.Heading>
              <ul className="tw-mb-0">
                {validationResults.messages.map((message, index) => (
                  <li key={index}>{message}</li>
                ))}
              </ul>
            </Alert>
            <p className="tw-mb-0">Please resolve these issues before closing the project.</p>
          </>
        ) : (
          <p className="tw-mb-0">This project will be marked as completed. Are you sure you want to proceed?</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          type="button"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={onConfirm}
          disabled={!validationResults.canClose}
        >
          Close Project
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CloseProjectModal; 